import { useCart } from "../context/AppContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../context/AppContext";
import { toast } from "sonner";
import { CartLine } from "./cart/CartLine";
import { ShippingForm, inp } from "./cart/ShippingForm";
import { CartSummary } from "./cart/CartSummary";

const EMPTY_SHIP = { full_name: "", email: "", phone: "", line1: "", line2: "", city: "", state: "", postcode: "", country: "AU" };
export const CRYPTO_TOKEN_PREFIX = "bl_crypto_";

function PromoField({ promoCode, setPromoCode, promo, applying, onApply }) {
  return (
    <div className="mt-6 border-t border-white/5 pt-5 space-y-3">
      <div className="bl-eyebrow">Promo Code</div>
      <div className="flex gap-2">
        <input data-testid="cart-promo" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="VIP10 / INFLUENCER15 / WELCOME5" className={inp} />
        <button onClick={onApply} disabled={applying} data-testid="cart-promo-apply" className="bl-btn text-xs px-4">{applying ? "..." : "Apply"}</button>
      </div>
      {promo && <div className="text-[color:var(--bl-green)] font-mono text-[10px] tracking-[0.25em] uppercase">● {promo.label} applied</div>}
    </div>
  );
}

export default function CartDrawer() {
  const { items, update, remove, subtotal, open, setOpen } = useCart();
  const [loading, setLoading] = useState(false); // false | 'manual' | 'crypto'
  const [step, setStep] = useState("cart"); // 'cart' | 'address'
  const [ship, setShip] = useState(EMPTY_SHIP);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [applying, setApplying] = useState(false);
  const [methods, setMethods] = useState({ manual: true, crypto: false });
  const idemKey = useRef(crypto.randomUUID());

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/payments/methods`).then(r => setMethods(r.data)).catch(() => {});
  }, [open]);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/promo/validate`, { code: promoCode });
      setPromo(r.data);
      toast.success(`${r.data.label} applied`);
    } catch { toast.error("Invalid code"); setPromo(null); }
    finally { setApplying(false); }
  };

  const discountedTotal = promo ? +(subtotal * (1 - promo.percent / 100)).toFixed(2) : subtotal;

  const payload = () => ({
    items: items.map(i => ({
      product_id: i.product_id, variant_id: i.variant_id, name: i.name,
      strength: i.strength, unit_price: i.unit_price, quantity: i.quantity,
    })),
    origin_url: window.location.origin,
    shipping: ship,
    promo_code: promo?.code || null,
  });

  const checkout = async () => {
    if (!items.length) return;
    setLoading("manual");
    try {
      const r = await axios.post(`${API}/payments/checkout`, payload(), { withCredentials: true });
      if (r.data.url) window.location.href = r.data.url;
      else toast.error("Order could not be placed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    } finally { setLoading(false); }
  };

  const cryptoCheckout = async () => {
    if (!items.length) return;
    setLoading("crypto");
    try {
      const r = await axios.post(`${API}/payments/crypto/checkout`, payload(),
        { withCredentials: true, headers: { "Idempotency-Key": idemKey.current } });
      const { order_id, access_token, checkout_url } = r.data;
      sessionStorage.setItem(CRYPTO_TOKEN_PREFIX + order_id, access_token);
      window.location.href = checkout_url || `${window.location.origin}/payment/crypto/return?order_id=${order_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bitcoin checkout unavailable right now");
      setLoading(false);
    }
  };

  const canProceed = ship.full_name && ship.email && ship.line1 && ship.city && ship.state && ship.postcode;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" side="right" className="w-full sm:max-w-md h-[100dvh] bg-black border-l border-white/10 text-white p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5">
          <SheetTitle className="font-display uppercase tracking-[0.2em] text-white text-lg">
            {step === "cart" ? "Your Cart" : "Ship To"}
          </SheetTitle>
          <div className="bl-eyebrow mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="h-full flex flex-col items-center justify-center text-center py-16">
              <img src="/assets/emblem.png" className="w-32 opacity-40 mb-6" alt="" />
              <div className="bl-eyebrow text-white/40">Nothing added yet</div>
              <button onClick={() => setOpen(false)} className="mt-6 bl-btn">Continue Shopping</button>
            </div>
          ) : step === "cart" ? (
            <>
              <ul className="divide-y divide-white/5">
                {items.map(i => <CartLine key={`${i.product_id}-${i.variant_id}`} item={i} update={update} remove={remove} />)}
              </ul>
              <PromoField promoCode={promoCode} setPromoCode={setPromoCode} promo={promo} applying={applying} onApply={applyPromo} />
            </>
          ) : (
            <ShippingForm ship={ship} setShip={setShip} onBack={() => setStep("cart")} />
          )}
        </div>

        {items.length > 0 && (
          <CartSummary
            items={items} subtotal={subtotal} promo={promo} discountedTotal={discountedTotal}
            step={step} onContinue={() => setStep("address")} onCheckout={checkout} onCrypto={cryptoCheckout}
            cryptoEnabled={methods.crypto} loading={loading} canProceed={canProceed}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
